import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, User, Rocket } from "lucide-react";
import { API_URL, TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SUGGESTIONS = [
  "Show me all hot leads.",
  "Which specialty cafés in Bangalore have I not contacted yet?",
  "Which leads look like premium coffee buyers?",
  "Draft a follow-up email for my top 3 warm leads.",
  "Who should I reach out to today?",
];

export default function GrowthChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm Prospect AI. Ask anything about your leads — I can filter, rank, or even draft outreach on the spot." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setInput("");
    setBusy(true);

    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const resp = await fetch(`${API_URL}/growth/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: q, session_id: sessionRef.current || undefined }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream error");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          let name = "", data = "";
          for (const l of ev.split("\n")) {
            if (l.startsWith("event:")) name = l.slice(6).trim();
            else if (l.startsWith("data:")) data += l.slice(5).trim();
          }
          if (!data) continue;
          try {
            const p = JSON.parse(data);
            if (name === "session") sessionRef.current = p.session_id;
            else if (p.token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: (copy[copy.length - 1]?.content || "") + p.token,
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't reach the AI. Try again in a moment." };
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="growth-chat-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          <Rocket className="h-3.5 w-3.5" /> Growth Engine · Prospect AI
        </div>
        <h1 className="font-display text-4xl mt-1">Talk to your pipeline.</h1>
        <p className="text-muted-foreground mt-1 text-sm max-w-lg">
          Ask any question in plain English — Prospect AI reads your CRM and answers with real leads.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card flex flex-col h-[64vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={"flex gap-3 " + (m.role === "user" ? "justify-end" : "justify-start")}>
              {m.role === "assistant" && (
                <div className="h-8 w-8 rounded-full bg-primary/20 text-primary grid place-items-center shrink-0"><Sparkles className="h-4 w-4" /></div>
              )}
              <div className={"max-w-[80%] text-sm rounded-2xl px-4 py-2.5 whitespace-pre-wrap " + (m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary")}>
                {m.content || (busy && m.role === "assistant" && i === messages.length - 1 ? "…" : "")}
              </div>
              {m.role === "user" && (
                <div className="h-8 w-8 rounded-full bg-foreground/10 grid place-items-center shrink-0"><User className="h-4 w-4" /></div>
              )}
            </div>
          ))}
        </div>

        {messages.length <= 1 && (
          <div className="px-5 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="text-xs rounded-full border border-border px-3 py-1.5 hover:bg-secondary" data-testid={`suggestion-${s.slice(0, 12)}`}>{s}</button>
            ))}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 flex items-center gap-2">
          <input
            value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about hot leads, cities, industries, next steps…"
            className="flex-1 h-11 rounded-full border border-border bg-background px-4 text-sm"
            disabled={busy}
            data-testid="growth-chat-input"
          />
          <Button type="submit" disabled={busy || !input.trim()} className="rounded-full h-11 w-11 p-0" data-testid="growth-chat-send">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
